import React, { useCallback, useEffect, useState } from "react";
import { Button, CircularProgress, TextField } from "@mui/material";
import { doc, updateDoc } from "@firebase/firestore";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";

import { PlainCard } from "../../../components/settings_components";
import { getCompanySettings } from "../../../utils/firebase_operations";
import { customFirestore } from "../../../firebase";
import { trackSentryException } from "../../../utils/helpers";
import { customAlertStateAtom } from "../../../components/custom-alert";
import { companyDataAtom } from "../../test-run/test-run-atoms";

const HANDBOOK_MAX_LENGTH = 60_000;

const AppHandbookPanel = ({ companyId }: { companyId: string | undefined }) => {
  const queryClient = useQueryClient();
  const setCustomAlertState = useSetAtom(customAlertStateAtom);

  const [handbook, setHandbook] = useState<string>("");

  const handbookQuery = useQuery({
    queryKey: ["getAppHandbook", companyId],
    queryFn: async () => {
      const firestoreSettings = await getCompanySettings(companyId);
      const storedHandbook =
        (firestoreSettings?.appHandbook as string | undefined) ?? "";
      return storedHandbook;
    },
    refetchOnWindowFocus: false,
    enabled: companyId != null,
  });

  useEffect(() => {
    if (handbookQuery.data !== undefined) {
      setHandbook(handbookQuery.data);
    }
  }, [handbookQuery.data]);

  const saveHandbookMutation = useMutation({
    mutationKey: ["saveAppHandbook", companyId],
    mutationFn: async () => {
      if (!companyId) {
        throw new Error("Missing company id");
      }
      const settingsRef = doc(customFirestore, "settings", companyId);
      await updateDoc(settingsRef, { appHandbook: handbook });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getAppHandbook", companyId],
      });
      if (companyId) {
        companyDataAtom.remove(companyId);
      }
      queryClient.removeQueries({ queryKey: ["getCompanyData"] });
      setCustomAlertState({
        open: true,
        type: "success",
        title: "App handbook saved successfully",
      });
    },
    onError: (error: any) => {
      trackSentryException(error);
      console.error("An error occurred during handbook saving", error);
      setCustomAlertState({
        open: true,
        type: "error",
        title: "Something went wrong",
        description: "An error occurred while saving the handbook",
      });
    },
  });

  const isDirty = handbook !== (handbookQuery.data ?? "");
  const isTooLong = handbook.length > HANDBOOK_MAX_LENGTH;

  const handleSave = useCallback(() => {
    saveHandbookMutation.mutate();
  }, [saveHandbookMutation]);

  return (
    <PlainCard>
      <div className="grid grid-cols-2">
        <h1 className="text-3xl pl-4 pb-2 font-bold">App Handbook</h1>
        <div className="place-self-end mt-2 pr-2">
          <Button
            variant="outlined"
            onClick={handleSave}
            size="large"
            disabled={
              !isDirty ||
              isTooLong ||
              saveHandbookMutation.isPending ||
              handbookQuery.isLoading
            }
          >
            Save
          </Button>
        </div>
      </div>
      <div className="pl-4 pr-4 pt-2 relative sm:rounded-lg overflow-auto h-[calc(100%-50.25px)]">
        <p className="text-sm text-gray-600 pb-4">
          Tacit knowledge about the app under test. This content is injected
          into the agent context at the start of every run, so use it to share
          notes such as where specific features live, naming quirks, or
          gotchas the agent should know about.
        </p>
        {handbookQuery.isLoading ? (
          <div className="flex flex-col h-full items-center justify-center w-full">
            <CircularProgress />
          </div>
        ) : (
          <TextField
            value={handbook}
            onChange={(event) => setHandbook(event.target.value)}
            multiline
            minRows={16}
            maxRows={32}
            fullWidth
            variant="outlined"
            placeholder="e.g. The login screen is reached by tapping the profile icon in the top-right of the home tab."
            error={isTooLong}
            helperText={
              isTooLong
                ? `Handbook is too long (${handbook.length}/${HANDBOOK_MAX_LENGTH} characters).`
                : `${handbook.length}/${HANDBOOK_MAX_LENGTH} characters`
            }
          />
        )}
      </div>
    </PlainCard>
  );
};

export { AppHandbookPanel };
